import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import IstresMoiTitle from "@/components/IstresMoiTitle";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import SiteFooter from "@/components/SiteFooter";
import { ISTRES_LOGO } from "@/lib/istres";
import { APP_VERSION, formatBuildDate } from "@/lib/build-info";
import {
  ArrowRight, Menu, X, ClipboardList, MapPin, Calendar,
  BookOpen, Lightbulb, Car, Newspaper, Store, Sparkles,
  UserPlus, User as UserIcon, Plus,
} from "lucide-react";

export const NAV_ITEMS = [
  { to: "/mur",           label: "Mur d'actus",   icon: Newspaper },
  { to: "/consultations", label: "Sondages",      icon: ClipboardList },
  { to: "/idees",         label: "Boîte à idées", icon: Lightbulb },
  { to: "/agenda",        label: "Agenda",        icon: Calendar },
  { to: "/plan",          label: "Plan d'Istres", icon: MapPin },
  { to: "/covoiturage",   label: "Co-voiturage",  icon: Car },
  { to: "/mes-avantages", label: "Avantages",     icon: Sparkles },
  { to: "/ressources",    label: "Ressources",    icon: BookOpen },
];

export default function PageShell({ children, lite = false }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isAuthed = !!user && user !== false;

  return (
    <div className="min-h-screen flex flex-col bg-cream-50">
      <PWAInstallBanner />
      <header className="sticky top-0 z-40 bg-cream-50/85 backdrop-blur-2xl border-b border-cream-200/60">
        <div className="max-w-6xl mx-auto pl-2 pr-2 sm:px-6 lg:px-8 h-[48px] sm:h-[56px] lg:h-[64px] flex items-center justify-between gap-2 sm:gap-3 relative">
          <Link to="/" className="shrink-0 z-10 -ml-3 sm:-ml-4 lg:-ml-6 flex items-center gap-0" aria-label="Istres & Moi · Accueil">
            <img src={ISTRES_LOGO} alt="Logo Ville d'Istres" className="h-9 sm:h-10 lg:h-12 w-auto object-contain -mt-1" data-testid="header-logo" />
            <div className="hidden md:flex items-center leading-none -ml-3 lg:-ml-4">
              <IstresMoiTitle className="text-plum-700 text-[20px] lg:text-[24px]" />
            </div>
          </Link>

          <div className="md:hidden absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex flex-col items-center leading-none pointer-events-none">
            <IstresMoiTitle className="text-plum-700 text-[17px] whitespace-nowrap" />
          </div>

          <div className="flex items-center gap-3 sm:gap-5 z-10 mr-1 sm:mr-0">
            <nav className="hidden lg:flex items-center gap-1 text-[13px] font-semibold text-plum-700/75 divide-x divide-plum-700/15">
              {(() => {
                // Desktop : Mur d'actus, Sondages, Boîte à idées, Agenda + Avantages (si connecté)
                const items = NAV_ITEMS.slice(0, 4);
                if (isAuthed) items.push(NAV_ITEMS.find((n) => n.to === "/mes-avantages"));
                return items.filter(Boolean).map((it) => {
                  const active = pathname === it.to;
                  return (
                    <Link key={it.to} to={it.to} className={`inline-flex items-center gap-1.5 px-3 first:pl-0 hover:text-plum-700 ${active ? "text-plum-700" : ""}`} data-testid={`top-nav-${it.to.replace("/", "")}`}>
                      <it.icon size={13} strokeWidth={2.2} />
                      {it.label}
                    </Link>
                  );
                });
              })()}
            </nav>

            {isAuthed ? (
              <Link to={user.role === "admin" ? "/admin" : user.role === "commercant" ? "/commercant" : "/habitant"} className="hidden sm:inline-flex items-center gap-2 pl-1 pr-3 h-10 rounded-full bg-white hover:bg-cream-50 border border-cream-200 shadow-sm">
                <span className="w-8 h-8 rounded-lg bg-plum-700 text-white font-display font-extrabold text-[14px] grid place-items-center">
                  {(user.pseudo || user.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="text-[12px] font-bold text-plum-900 max-w-[120px] truncate">{user.pseudo || user.name?.split(" ")[0]}</span>
              </Link>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  to="/register"
                  data-testid="header-register-btn"
                  className="group inline-flex items-center gap-2 h-10 pl-1 pr-4 rounded-full bg-white border border-plum-700/15 hover:border-plum-700/40 hover:bg-cream-50 text-plum-700 text-[12px] font-bold transition-all shadow-sm hover:shadow"
                >
                  <span className="w-8 h-8 rounded-full bg-white text-sun-500 grid place-items-center shadow-inner border border-sun-300/40">
                    <Plus size={16} strokeWidth={3.2} />
                  </span>
                  <span className="leading-none">
                    Créer un compte
                    <span className="block text-[9px] font-semibold text-mint-600 uppercase tracking-wider">Gratuit</span>
                  </span>
                </Link>
                <Link
                  to="/login"
                  data-testid="header-login-btn"
                  className="group inline-flex items-center gap-2 h-10 pl-1 pr-4 rounded-full bg-gradient-to-r from-plum-700 to-plum-800 hover:from-plum-800 hover:to-plum-900 text-white text-[12px] font-bold transition-all shadow-lg shadow-plum-700/25 hover:shadow-xl hover:-translate-y-0.5"
                >
                  <span className="w-8 h-8 rounded-full bg-white/15 text-white grid place-items-center backdrop-blur-sm">
                    <UserIcon size={14} strokeWidth={2.4} />
                  </span>
                  <span className="leading-none">
                    Se connecter
                    <span className="block text-[9px] font-semibold text-cream-50/70 uppercase tracking-wider">Habitant · Commerçant</span>
                  </span>
                </Link>
              </div>
            )}

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full bg-plum-700 hover:bg-plum-800 text-white grid place-items-center shadow-lg shadow-plum-700/30 transition-transform active:scale-95"
              aria-label="Menu"
            >
              {open ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-white/97 backdrop-blur-2xl border-t border-cream-200/60 shadow-xl" data-testid="mobile-menu-drawer">
            <nav className="flex flex-col p-3 max-w-2xl mx-auto">
              {NAV_ITEMS.map((it) => (
                <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className="flex items-center gap-3 p-3 rounded-2xl text-plum-900 hover:bg-cream-50 active:bg-cream-100" data-testid={`mobile-nav-${it.to.replace("/", "")}`}>
                  <div className="w-11 h-11 rounded-2xl bg-plum-100 text-plum-700 grid place-items-center shrink-0">
                    <it.icon size={19} strokeWidth={2.2} />
                  </div>
                  <span className="font-display font-bold text-[16px]">{it.label}</span>
                  <ArrowRight size={15} className="ml-auto text-plum-400" />
                </Link>
              ))}
              {!isAuthed && (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-cta-login" className="btn-pill bg-plum-700 text-white h-14 mt-3 mx-2 text-[16px] shadow-lg shadow-plum-700/25">
                  Se connecter <ArrowRight size={17} />
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {!lite && <SiteFooter />}
    </div>
  );
}
